import {
  PurchaseOrder,
  PurchaseOrderState,
  PurchaseOrderLineItem,
  PurchaseOrderLineItemReceipt,
  DiscountRuleType,
} from "../types/api";
import { LineItemStatus } from "../types/lineItemTypes";

import {
  Backdrop,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import {
  deletePurchaseOrder,
  deletePurchaseOrderLineReceipts,
  getPurchaseOrder,
  receivePurchaseOrderLines,
  updatePurchaseOrder,
} from "../services/purchasing.service";
import { createBillFromReceipts } from "../services/bill.service";
import PurchaseOrderLineItemTable from "../components/po-lineitem-table.component";
import GlobalMessageContext from "../context/globalMessage.context";
import StateChip from "../components/state-chip.component";
import ButtonWithConfirm from "../components/modal.confirm-dialog.component";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EditPurchaseOrderRoute, PurchaseOrdersRoute } from "../routes";
import _ from "lodash";
import axios from "axios";
import { PURCHASING_API } from "../config";
import { getInventoryForVariants, updateVariantStockLevelAndPrices } from "../services/product.service";
import { POSummary } from "../components/po-summary.component";
import { useUserRole } from "../hooks/useUserRole";

const ViewPurchaseOrderView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isAdmin } = useUserRole();
  const poNumber = state["selectedPo"];
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('md'));

  // PO to be viewed should be passed as selectedPo, so redirect to the full list if it's not.
  if (_.isEmpty(poNumber)) {
    navigate(PurchaseOrdersRoute);
  }

  // Load the selectedPo into a state variable
  const [selectedPo, setSelectedPo] = useState<PurchaseOrder>();
  const [rowsToReceive, setRowsToReceive] = useState([]);

  const uxMessage = useContext(GlobalMessageContext);

  useEffect(() => {
    getPurchaseOrder(poNumber).then((po) => {
      const variants = po.lineItems
        .filter((li) => li.variantGuid)
        .map((li) => li.variantGuid);

      // If the PO is ONLY SPEs we don't bother trying to get stock levels
      if (variants.length > 0) {
        getInventoryForVariants(variants, (inv) => {
          po.lineItems.forEach((li) => {
            const v = inv.find((i) => i.guid === li.variantGuid);
  
            // v may be null as some items may be SPEs or no longer in AMS
            li['stockOnHand'] = v?.stockOnHand || 0;  
          });

          setSelectedPo(po);
        });
      }
      else {
        setSelectedPo(po)
      }
    });
  }, [poNumber]);

  const onDelete = () => {
    const isDraft = selectedPo.state === PurchaseOrderState.DRAFT;
    const actionName = isDraft ? "deleted" : "voided";
    
    _deleteReceipts(
      selectedPo.lineItems.reduce((a, b) => [...a, b.receipts], [])
    ).then(() => {
      deletePurchaseOrder({
        guid: selectedPo.guid,
        success: () => {
          navigate(PurchaseOrdersRoute);
          uxMessage.setMessage({
            message: `Purchase Order ${isDraft ? "Deleted" : "Voided"}`,
            severity: "success",
          });
        },
        failure: () => {
          uxMessage.setMessage({
            message: `Sorry - something went wrong!`,
            severity: "error",
          });
        },
      });
    });
  };

  const _updatePO = (po: PurchaseOrder) => {
    updatePurchaseOrder({
      po: po,
      success: () => {
        uxMessage.setMessage({
          severity: "success",
          message: `${po.number} updated successfully`,
        });
        setSelectedPo(po);
      },
      failure: () =>
        uxMessage.setMessage({
          severity: "error",
          message: "Sorry - something went wrong",
        }),
    });
  };

  const onMakeActive = () => {
    if (!selectedPo.supplier) {
      uxMessage.setMessage({ message: "A supplier must be set before making a PO active", severity: "warning" });
      return;
    }
    const po = JSON.parse(JSON.stringify(selectedPo, (k, v) => k === "stockOnHand" ? undefined : v));
    _updatePO({...po, state: PurchaseOrderState.ACTIVE});
  };

  const onReceive = () => {
    const receipts = rowsToReceive.map((r) => {
      // r.id corresponds to the tempId, which is item.guid || `temp-${item.id}`
      const poli = selectedPo.lineItems.filter((li) => {
        const tempId = li.guid || `temp-${li.id}`;
        return tempId === r.id;
      })[0];
      
      return {
        poli,
        receipt: {
          date: new Date(),
          received: r.quantityToReceive,
          receivedPrice: +r.receivePrice.replace(/[^0-9]/g, ""),
        },
      };
    });

    if (!_.isEmpty(receipts.filter((r) => r.receipt.receivedPrice === 0))) {
      // Cannot proceed without prices for all lines
      uxMessage.setMessage({
        severity: "error",
        message: "Please ensure that all lines have a Received At price set",
      });

      return;
    }

    receivePurchaseOrderLines({
      receipts: receipts,
      success: (r) => {
        // Now update the stock levels for those things received
        _updateStockLevelsAndPrices(receipts)
          .then(() => {
            // Finally make changes made to the POLI permanent, but remove the
            // state as this may have been changed behind the scenes and we don't
            // want to overwrite it
            updatePurchaseOrder({
              po: { ...selectedPo, state: undefined },
              success: () => {
                // Reload the PO data to reflect the updated state
                getPurchaseOrder(poNumber).then((updatedPo) => {
                  const variants = updatedPo.lineItems
                    .filter((li) => li.variantGuid)
                    .map((li) => li.variantGuid);

                  // If the PO is ONLY SPEs we don't bother trying to get stock levels
                  if (variants.length > 0) {
                    getInventoryForVariants(variants, (inv) => {
                      updatedPo.lineItems.forEach((li) => {
                        const v = inv.find((i) => i.guid === li.variantGuid);
                        li['stockOnHand'] = v?.stockOnHand || 0;
                      });
                      setSelectedPo(updatedPo);
                    });
                  } else {
                    setSelectedPo(updatedPo);
                  }
                });
              },
            });

            uxMessage.setMessage({
              severity: "success",
              message: `${receipts.length} lines received successfully`,
            });

            // Stay on the current page instead of navigating away
            setRowsToReceive([]);
          })
          .catch((e) => {
            console.error(e);
            uxMessage.setMessage({
              severity: "error",
              message: e.toString(),
            });
          });
      },
      failure: (e) => {
        console.error(e);
        uxMessage.setMessage({
          severity: "error",
          message: e?.response?.data?.message || e.toString(),
        });
      },
    });
  };

  const _deleteReceipts = async (receipts: PurchaseOrderLineItemReceipt[]) => {
    deletePurchaseOrderLineReceipts({
      receipts,
      success: (r) => {
        // Now revert the stock levels for things in that receipt
        _updateStockLevelsAndPrices(
          receipts.map((r) => {
            r.received = -r.received;
            return { poli: r.poLineItem, receipt: r };
          })
        ).then(() => {
          uxMessage.setMessage({
            severity: "success",
            message: `${r.length} lines reverted successfully`,
          });
        });
      },
      failure: (e) => {
        console.error(e);
        uxMessage.setMessage({
          severity: "error",
          message: e,
        });
      },
    });
  };

  const _createBill = async (receipts: PurchaseOrderLineItemReceipt[]) => {
    try {
      const receiptGuids = receipts.map((r) => r.guid);
      await createBillFromReceipts(receiptGuids, selectedPo.guid);

      uxMessage.setMessage({
        severity: "success",
        message: "Bill created successfully in Xero",
      });
    } catch (error: any) {
      console.error(error);
      uxMessage.setMessage({
        severity: "error",
        message: `Failed to create bill: ${error.response?.data?.message || error.message}`,
      });
    }
  };

  const _updateStockLevelsAndPrices = async (receipts) => {
    const promises: Promise<any>[] = [];

    receipts.forEach((receipt) => {
      // Skip stock updates for SPEs or line items without variant GUID (historical/deleted variants)
      if (!receipt.poli.variantSku.startsWith("SPE") && receipt.poli.variantGuid) {
        promises.push(
          updateVariantStockLevelAndPrices(
            receipt.poli.variantGuid,
            receipt.receipt.received * (receipt.poli.packSize || 1),
            receipt.poli.retailPriceTmp,
            receipt.receipt.receivedPrice
          )
        );
      }
    });

    return await Promise.all(promises);
  };

  const _rowsChanged = (rows) => {
    setRowsToReceive(rows);

    // Update the selectedPO in memory
    rows.forEach((r) => {
      // r.id corresponds to the tempId, which is item.guid || `temp-${item.id}`
      const poli: PurchaseOrderLineItem = selectedPo.lineItems.filter(
        (poli) => {
          const tempId = poli.guid || `temp-${poli.id}`;
          return tempId === r.id;
        }
      )[0];

      if (poli) {
        poli.itemCost = +r.receivePrice.replace(/[^0-9]/g, "");

        // Retail prices are read from the inventory service, and referenced, so this temporary field is a
        // place to hold it to drive the front end, but also enable us to call that service when the item is
        // received.
        poli.retailPriceTmp = +r.retailPrice.replace(/[^0-9]/g, "");
      }
    });
  };

  const getDiscountBanners = (po: PurchaseOrder) => {
    if (po.state !== PurchaseOrderState.DRAFT) return [];
    if (!po.supplier?.discountRules) return [];

    return po.supplier.discountRules
      .filter((rule) => rule.active)
      .map((rule) => {
        if (rule.ruleType === DiscountRuleType.FREE_DELIVERY_THRESHOLD) {
          const threshold = (rule.configuration as any).threshold;
          const current = po.total;
          const met = current >= threshold;
          return {
            severity: (met ? "success" : "error") as "success" | "error",
            message: met
              ? `Free delivery threshold reached. Threshold: £${(threshold / 100).toFixed(2)}, Current: £${(current / 100).toFixed(2)}`
              : `Free delivery not yet reached. Threshold: £${(threshold / 100).toFixed(2)}, Current: £${(current / 100).toFixed(2)}`,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await axios.get(url, {
        responseType: 'blob', // Important: tells axios to expect binary data
      });

      // Create a temporary URL for the blob
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);

      // Create temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      uxMessage.setMessage({
        message: `Failed to download ${filename}`,
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Backdrop open={!selectedPo}>
        <CircularProgress />
      </Backdrop>
      {selectedPo && (
        <>
          <Box sx={{ m: 1 }}>
            <Grid container>
              <Grid size={12}>
                <Typography variant="h2" align="left">
                  <Link to={PurchaseOrdersRoute}>Purchase Orders</Link>
                  &nbsp;&gt;&nbsp;{selectedPo.number}
                </Typography>
                <Divider sx={{ margin: 1 }} />
              </Grid>
              <Grid size={isNarrowScreen ? 12 : 6}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell variant="body" align="left">
                          <Typography variant="h5">
                            {selectedPo.supplier?.name ?? '—'}&nbsp;{" "}
                            <StateChip state={selectedPo.state} size="small" />
                          </Typography>
                          &nbsp;
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid size={isNarrowScreen ? 12 : 6}>
                <Box
                  sx={{ m: 2, p: 0 }}
                  display="flex"
                  justifyContent={isNarrowScreen ? "flex-start" : "flex-end"}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ alignItems: "center" }}
                  >
                    {/* Receive button */}
                    {selectedPo.state === PurchaseOrderState.ACTIVE &&
                      // Only show the button if we have stuff left to receive
                      selectedPo.lineItems
                        .map(
                          (poli: PurchaseOrderLineItem) => poli.quantityOrdered
                        )
                        .reduce((a, b) => a + b, 0) >
                        selectedPo.lineItems
                          .filter(
                            (poli: PurchaseOrderLineItem) =>
                              !_.isEmpty(poli.receipts)
                          )
                          .map((poli: PurchaseOrderLineItem) => poli.receipts)
                          .reduce((a, b) => [...a, ...b], [])
                          .map(
                            (receipt: PurchaseOrderLineItemReceipt) =>
                              receipt.received
                          )
                          .reduce((a, b) => a + b, 0) && (
                        <Tooltip title="Receive the checked items on this PO">
                          <ButtonWithConfirm
                            disabled={_.isEmpty(rowsToReceive)}
                            size="small"
                            color="primary"
                            variant="contained"
                            message={`This will record a new goods receipt for ${selectedPo.number}.`}
                            onOk={() => onReceive()}
                          >
                            Receive{" "}
                            {rowsToReceive.length > 0
                              ? `(${rowsToReceive.length} row${
                                  rowsToReceive.length !== 1 ? "s" : ""
                                }) `
                              : ``}
                          </ButtonWithConfirm>
                        </Tooltip>
                      )}

                    {/* Download CSV button */}
                    <Tooltip title="Download CSV">
                      <Button
                        size="small"
                        variant="contained"
                        color="inherit"
                        onClick={() => {
                          downloadFile(
                            `${PURCHASING_API}csv/${selectedPo.number}`,
                            `${selectedPo.number}.csv`
                          );
                        }}
                      >
                        Download CSV
                      </Button>
                    </Tooltip>

                    {/* Download PDF button */}
                    <Tooltip title={selectedPo.state === PurchaseOrderState.DRAFT ? "PO is not active" : "Download PDF"}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="inherit"
                          disabled={selectedPo.state === PurchaseOrderState.DRAFT}
                          onClick={() => {
                            downloadFile(
                              `${PURCHASING_API}pdf/${selectedPo.number}`,
                              `${selectedPo.number}.pdf`
                            );
                          }}
                        >
                          Download PDF
                        </Button>
                      </span>
                    </Tooltip>

                    {/* Make Active button */}
                    {selectedPo.state === PurchaseOrderState.DRAFT && (
                      <Tooltip title="Make Active">
                        <ButtonWithConfirm
                          size="small"
                          variant="contained"
                          message={`This will make ${selectedPo.number} active - the PO will be sent to Xero.`}
                          color="inherit"
                          onOk={() => onMakeActive()}
                          okButtonLabel="Make Active"
                        >
                          Make Active
                        </ButtonWithConfirm>
                      </Tooltip>
                    )}

                    {/* Edit button */}
                    {selectedPo.state !== PurchaseOrderState.RECEIVED && (
                      <Tooltip title="Make changes to the Purchase Order">
                        <Button
                          size="small"
                          variant="contained"
                          color="inherit"
                          onClick={() =>
                            navigate(EditPurchaseOrderRoute, {
                              state: {
                                po: selectedPo,
                              },
                            })
                          }
                        >
                          Edit Order
                        </Button>
                      </Tooltip>
                    )}

                    {/* Delete/Void button */}
                    {(() => {
                      const getDeleteButtonConfig = () => {
                        switch (selectedPo.state) {
                          case PurchaseOrderState.DRAFT:
                            return {
                              label: "Delete",
                              tooltip: "Delete this Purchase Order",
                              message: `This will permanently delete Purchase Order ${selectedPo.number}!`,
                              disabled: false,
                              color: "primary" as const
                            };
                          case PurchaseOrderState.ACTIVE:
                            return {
                              label: "Void",
                              tooltip: "Void this Purchase Order",
                              message: `This will void Purchase Order ${selectedPo.number}!`,
                              disabled: false,
                              color: "primary" as const
                            };
                          case PurchaseOrderState.RECEIVED:
                            return {
                              label: "Delete",
                              tooltip: "Received POs cannot be deleted",
                              message: "",
                              disabled: true,
                              color: "inherit" as const
                            };
                          case PurchaseOrderState.VOID:
                            return {
                              label: "Delete",
                              tooltip: "Void PO cannot be deleted",
                              message: "",
                              disabled: true,
                              color: "inherit" as const
                            };
                          default:
                            return {
                              label: "Delete",
                              tooltip: "Delete this Purchase Order",
                              message: `This will delete Purchase Order ${selectedPo.number}!`,
                              disabled: false,
                              color: "primary" as const
                            };
                        }
                      };
                      
                      const config = getDeleteButtonConfig();
                      
                      return (
                        <Tooltip title={config.tooltip}>
                          <span>
                            <ButtonWithConfirm
                              size="small"
                              variant="contained"
                              message={config.message}
                              color={config.color}
                              disabled={config.disabled}
                              onOk={config.disabled ? undefined : () => onDelete()}
                              okButtonLabel={config.label}
                            >
                              {config.label}
                            </ButtonWithConfirm>
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </Stack>
                </Box>
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              {getDiscountBanners(selectedPo).map((banner, i) => (
                <Grid size={12} key={i}>
                  <Alert severity={banner.severity} sx={{ mt: 1 }}>
                    {banner.message}
                  </Alert>
                </Grid>
              ))}
              <Grid size={4}>
                <POSummary po={selectedPo} />
              </Grid>
            </Grid>

            <PurchaseOrderLineItemTable
              items={selectedPo.lineItems.map(item => ({
                ...item,
                tempId: item.guid || `temp-${item.id}`,
                status: LineItemStatus.ACTIVE
              }))}
              parentPo={selectedPo}
              hideReceiveCols={selectedPo.state === PurchaseOrderState.DRAFT}
              onRowsToReceiveChange={(rows) => _rowsChanged(rows)}
              onDeleteReceipts={(receipts) =>
                _deleteReceipts(receipts).then(() => {
                  // Reload the PO data to reflect the updated state
                  getPurchaseOrder(poNumber).then((updatedPo) => {
                    const variants = updatedPo.lineItems
                      .filter((li) => li.variantGuid)
                      .map((li) => li.variantGuid);

                    // If the PO is ONLY SPEs we don't bother trying to get stock levels
                    if (variants.length > 0) {
                      getInventoryForVariants(variants, (inv) => {
                        updatedPo.lineItems.forEach((li) => {
                          const v = inv.find((i) => i.guid === li.variantGuid);
                          li['stockOnHand'] = v?.stockOnHand || 0;
                        });
                        setSelectedPo(updatedPo);
                      });
                    } else {
                      setSelectedPo(updatedPo);
                    }
                  });
                })
              }
              onCreateBill={isAdmin ? (receipts) => _createBill(receipts) : undefined}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default ViewPurchaseOrderView;
